import InputText from "components/shared/inputs/input-text";
import Breadcrumb from "components/shared/breadcrumb";
import SwitchButton from "components/shared/switchButton";
import UseFetch from "hooks/useFetch";
import { memo, useEffect, useState } from "react";
import Skeleton from "./skeleton";
import { faNumToEng, numberRegex } from "utils";
import { CSSTransition } from "react-transition-group";
import { toast } from "react-toastify";
import MasterAPI from "service/master";

const DemoAudioDuration = () => {
    const { isLoading, error, data } = UseFetch("/api/setting/upload/audio/demo_duration/all");

    const [deletedIds, setDeletedIds] = useState([]);
    const [durations, setDuraions] = useState([]);
    const [localLoading, setLocalLoading] = useState(true);
    const [failedFields, setFailedFields] = useState({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitEnabled, setSubmitEnabled] = useState(false);

    useEffect(() => {
        if (!isLoading && !error) {
            setDuraions(data.data || []);
            setLocalLoading(false);
        }
    }, [isLoading, error, data])

    const addHandler = () => {
        if (isLoading || error)
            return;

        setDuraions(prev => [
            ...prev,
            {
                duration: null,
                status_id: 1,
            }
        ]);

        setSubmitEnabled(true);
    }

    const removeHandler = index => {
        const intendedSize = durations[index];
        if (intendedSize.id)
            setDeletedIds(prev => [...prev, intendedSize.id]);

        setDuraions(prev => prev.filter((_, i) => i !== index));
        setSubmitEnabled(true);
    }

    const durationChangeHandler = (e, index) => {
        let newValue = e.target.value;
        const isEmpty = newValue === "";

        if (numberRegex.test(newValue) || isEmpty) {
            newValue = !isEmpty ? parseInt(faNumToEng(newValue)) : "";
            if (!isEmpty && newValue > Number.MAX_SAFE_INTEGER)
                return;

            setDuraions(prev => {
                const newState = [...prev];
                newState[index]["duration"] = newValue;

                return newState;
            });

            setFailedFields(prev => {
                const newState = { ...prev };
                delete newState[index];

                return newState;
            });

            setSubmitEnabled(true);
        }
    }

    const labelChangeHandler = (e, index) => {
        setDuraions(prev => {
            const newState = [...prev];
            newState[index]["label"] = e.target.value.length ? e.target.value : null;

            return newState;
        });

        setSubmitEnabled(true);
    }

    const statusChangeHandler = index => {
        setDuraions(prev => {
            const newState = [...prev];
            newState[index]["status_id"] = newState[index]["status_id"] === 1 ? 0 : 1;

            return newState;
        });

        setSubmitEnabled(true);
    }

    const submitHandler = async () => {
        if (submitLoading || error || localLoading || !submitEnabled)
            return;

        const newFailedFields = {};
        durations.forEach((size, i) => {
            const thisErrors = [];

            if (!size.duration > 0)
                thisErrors.push("duration");

            if (thisErrors.length > 0)
                newFailedFields[i] = thisErrors;
        });

        setFailedFields(newFailedFields);
        if (Object.keys(newFailedFields).length === 0) {
            setSubmitLoading(true);

            try {
                const res = await MasterAPI({
                    url: "/api/setting/upload/audio/demo_duration/edit_multi",
                    data: {
                        deleted_ids: deletedIds,
                        demo_audio_durations: durations
                    },
                    callback: () => setSubmitLoading(false),
                    payloadType: 3
                });

                toast.success(res.message);
                setSubmitEnabled(false);
            } catch (e) {
                toast.error(e.message);
            }
        }
    }

    return (
        <div className="m-container">
            <div className="flex item-start md:items-center justify-start md:justify-between flex-col md:flex-row mb-6 options-bar">
                <div className="mb-6 md:mb-0">
                    <h1 className="title-c1 tp-21">دمو فایل های صوتی</h1>

                    <Breadcrumb data={[
                        { title: "تنظیمات فروشگاه" },
                        { title: "تنظیمات سرور", link: "/panel/server-settings" },
                        { title: "دمو فایل های صوتی" }
                    ]} />
                </div>

                <div className="flex">
                    <button
                        className="w-10 h-10 text-white bg-primary-100 hover:bg-primary-200 rounded-md fit-center"
                        title="افزودن سایز"
                        onClick={addHandler}
                    >
                        <i className="fa-solid fa-plus"></i>
                    </button>

                    <button
                        className={`bg-primary-100 px-3 transition-all text-white rounded-md fit-center text-sm h-10 mr-3 relative overflow-hidden ${!submitEnabled ? "opacity-50 cursor-default" : "hover:bg-primary-200"}`}
                        onClick={submitHandler}
                    >
                        <i className="fa-light fa-turn-down-left ml-2"></i>
                        ثبت تغییرات

                        <CSSTransition unmountOnExit={true} in={submitLoading} timeout={200}>
                            <div className="pos-cover anim-c4 fit-center text-white bg-primary-200">
                                <i className="fa-solid fa-spinner spin"></i>
                            </div>
                        </CSSTransition>
                    </button>
                </div>
            </div>

            {
                !localLoading ? (
                    !error ? (
                        durations.length ? (
                            durations.map((size, i) => (
                                <div
                                    className={`wrap-box tp-22 page-transition flex flex-col items-stretch md:flex-row md:items-center mb-3 crop-size-block ${failedFields[i] ? "has-error" : ""}`}
                                    key={`duration-${i}`}
                                >
                                    <div className="flex md:ml-3 grow my-2 md:my-0">
                                        <div className="ml-3 grow">
                                            <InputText
                                                label="بازه زمانی (ثانیه)"
                                                masterClass="w-full"
                                                id={`duration-${i}-duration`}
                                                value={size.duration || ""}
                                                changeHandler={e => durationChangeHandler(e, i)}
                                            />

                                            {
                                                failedFields[i] && failedFields[i]?.indexOf("duration") !== -1 ? <p className="local-error">بازه زمانی باید به صورت عدد وارد شود</p> : <></>
                                            }
                                        </div>
                                    </div>

                                    <div className="flex justify-between mt-3 md:mt-0">
                                        <div className="flex items-center ml-3">
                                            <SwitchButton
                                                checked={size.status_id === 1}
                                                changeHandler={() => statusChangeHandler(i)}
                                                id={`duration-${i}-status`}
                                            />

                                            <label
                                                className="tp-6 text-sm font-medium mr-2 cursor-pointer noselect"
                                                htmlFor={`duration-${i}-status`}
                                            >
                                                وضعیت
                                            </label>
                                        </div>

                                        <button
                                            className="bg-red-600 hover:bg-red-700 transition-colors rounded-md text-white text-sm px-3 py-1"
                                            onClick={() => removeHandler(i)}
                                        >
                                            حذف
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="wrap-box tp-22 page-transition">
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    <i className="fa-light fa-clipboard-list ml-2 relative top-px"></i>
                                    موردی برای نمایش یافت نشد
                                </p>
                            </div>
                        )
                    ) : (
                        <div className="wrap-box tp-22 page-transition">
                            <p className="text-sm text-red-600 dark:text-red-400">خطا در دریافت اطلاعات !</p>
                        </div>
                    )
                ) : <Skeleton />
            }
        </div>
    )
}

export default memo(DemoAudioDuration);